import { useEffect, useState } from "react";
import { useLocation } from "@/lib/router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AGENT_ADAPTER_TYPES } from "@ligament/shared";
import { usePractice } from "../context/PracticeContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { useConfirm } from "../context/ConfirmContext";
import { companiesApi } from "../api/companies";
import { accessApi } from "../api/access";
import { authApi } from "../api/auth";
import { notificationPrefsApi, type NotificationPrefs } from "../api/notificationPrefs";
import {
  integrationsApi,
  type IntegrationProvider,
  type IntegrationStatus,
} from "../api/integrations";
import { webhooksApi, type WebhookRegistrationWithSecret } from "../api/webhooks";
import { queryKeys } from "../lib/queryKeys";
import { Button } from "@/components/ui/button";
import {
  Settings,
  Check,
  Shield,
  ShieldCheck,
  Link2,
  Bell,
  Lock,
  Building2,
  Phone,
  Image,
  MapPin,
  Bot,
  Ticket,
  Monitor,
  DollarSign,
  Webhook,
  BarChart3,
  Eye,
  EyeOff,
} from "lucide-react";
import { CompanyPatternIcon } from "../components/CompanyPatternIcon";
import {
  Field,
  ToggleField,
  HintIcon,
  adapterLabels,
} from "../components/agent-config-primitives";

type AgentSnippetInput = {
  onboardingTextUrl: string;
  connectionCandidates?: string[] | null;
  testResolutionUrl?: string | null;
};

type SettingsTab = "general" | "integrations" | "webhooks" | "notifications" | "security";

const TABS: Array<{ id: SettingsTab; label: string; icon: typeof Settings }> = [
  { id: "general", label: "General", icon: Building2 },
  { id: "integrations", label: "Integrations", icon: Link2 },
  { id: "webhooks", label: "Webhooks", icon: Webhook },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "security", label: "Security", icon: Lock },
];

export function CompanySettings() {
  const {
    companies,
    selectedPractice,
    selectedPracticeId,
    setSelectedPracticeId,
  } = usePractice();
  const { setBreadcrumbs } = useBreadcrumbs();
  const queryClient = useQueryClient();
  const location = useLocation();

  // Derive initial tab from route
  const initialTab: SettingsTab = location.pathname.includes("/integrations")
    ? "integrations"
    : location.pathname.includes("/ehr-integration")
      ? "integrations"
      : location.pathname.includes("/ai-config")
        ? "general"
        : location.pathname.includes("/schema-builder")
          ? "general"
          : "general";
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);

  // Sync tab when navigating between /integrations and /company/settings
  useEffect(() => {
    const tab: SettingsTab = location.pathname.includes("/integrations") ? "integrations" : "general";
    setActiveTab(tab);
  }, [location.pathname]);

  // General settings local state
  const [companyName, setCompanyName] = useState("");
  const [description, setDescription] = useState("");
  const [brandColor, setBrandColor] = useState("");

  // Sync local state from selected company
  useEffect(() => {
    if (!selectedPractice) return;
    setCompanyName(selectedPractice.name);
    setDescription(selectedPractice.description ?? "");
    setBrandColor(selectedPractice.brandColor ?? "");
  }, [selectedPractice]);

  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSnippet, setInviteSnippet] = useState<string | null>(null);
  const [snippetCopied, setSnippetCopied] = useState(false);
  const [snippetCopyDelightId, setSnippetCopyDelightId] = useState(0);

  const generalDirty =
    !!selectedPractice &&
    (companyName !== selectedPractice.name ||
      description !== (selectedPractice.description ?? "") ||
      brandColor !== (selectedPractice.brandColor ?? ""));

  const generalMutation = useMutation({
    mutationFn: (data: {
      name: string;
      description: string | null;
      brandColor: string | null;
    }) => companiesApi.update(selectedPracticeId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
    },
  });

  const settingsMutation = useMutation({
    mutationFn: (requireApproval: boolean) =>
      companiesApi.update(selectedPracticeId!, {
        requireBoardApprovalForNewAgents: requireApproval,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
    },
  });

  const inviteMutation = useMutation({
    mutationFn: () =>
      accessApi.createGatewayInvitePrompt(selectedPracticeId!),
    onSuccess: async (invite) => {
      setInviteError(null);
      const base = window.location.origin.replace(/\/+$/, "");
      const onboardingTextLink =
        invite.onboardingTextUrl ??
        invite.onboardingTextPath ??
        `/api/invites/${invite.token}/onboarding.txt`;
      const absoluteUrl = onboardingTextLink.startsWith("http")
        ? onboardingTextLink
        : `${base}${onboardingTextLink}`;
      setSnippetCopied(false);
      setSnippetCopyDelightId(0);
      let snippet: string;
      try {
        const manifest = await accessApi.getInviteOnboarding(invite.token);
        snippet = buildAgentSnippet({
          onboardingTextUrl: absoluteUrl,
          connectionCandidates:
            manifest.onboarding.connectivity?.connectionCandidates ?? null,
          testResolutionUrl:
            manifest.onboarding.connectivity?.testResolutionEndpoint?.url ??
            null,
        });
      } catch {
        snippet = buildAgentSnippet({
          onboardingTextUrl: absoluteUrl,
          connectionCandidates: null,
          testResolutionUrl: null,
        });
      }
      setInviteSnippet(snippet);
      try {
        await navigator.clipboard.writeText(snippet);
        setSnippetCopied(true);
        setSnippetCopyDelightId((prev) => prev + 1);
        setTimeout(() => setSnippetCopied(false), 2000);
      } catch {
        /* clipboard may not be available */
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.sidebarBadges(selectedPracticeId!),
      });
    },
    onError: (err) => {
      setInviteError(
        err instanceof Error ? err.message : "Failed to create invite",
      );
    },
  });

  useEffect(() => {
    setInviteError(null);
    setInviteSnippet(null);
    setSnippetCopied(false);
    setSnippetCopyDelightId(0);
  }, [selectedPracticeId]);

  const archiveMutation = useMutation({
    mutationFn: ({
      companyId,
      nextCompanyId,
    }: {
      companyId: string;
      nextCompanyId: string | null;
    }) => companiesApi.archive(companyId).then(() => ({ nextCompanyId })),
    onSuccess: async ({ nextCompanyId }) => {
      if (nextCompanyId) {
        setSelectedPracticeId(nextCompanyId);
      }
      await queryClient.invalidateQueries({
        queryKey: queryKeys.companies.all,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.companies.stats,
      });
    },
  });

  useEffect(() => {
    setBreadcrumbs([
      { label: selectedPractice?.name ?? "Practice", href: "/dashboard" },
      { label: "Settings" },
    ]);
  }, [setBreadcrumbs, selectedPractice?.name]);

  if (!selectedPractice) {
    return (
      <div className="text-sm text-muted-foreground">
        No practice selected. Select a practice from the switcher above.
      </div>
    );
  }

  function handleSaveGeneral() {
    generalMutation.mutate({
      name: companyName.trim(),
      description: description.trim() || null,
      brandColor: brandColor || null,
    });
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Settings</h1>
          <p className="text-sm text-muted-foreground">
            Configure your practice settings
          </p>
        </div>
        {activeTab === "general" && generalDirty && (
          <Button
            onClick={handleSaveGeneral}
            disabled={generalMutation.isPending || !companyName.trim()}
            className="gap-1.5"
          >
            <Settings className="h-3.5 w-3.5" />
            {generalMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-border">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                isActive
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === "general" && (
        <GeneralTab
          companyName={companyName}
          setCompanyName={setCompanyName}
          description={description}
          setDescription={setDescription}
          brandColor={brandColor}
          setBrandColor={setBrandColor}
          selectedPractice={selectedPractice}
          settingsMutation={settingsMutation}
          generalMutation={generalMutation}
          archiveMutation={archiveMutation}
          companies={companies}
          selectedPracticeId={selectedPracticeId}
          setSelectedPracticeId={setSelectedPracticeId}
        />
      )}

      {activeTab === "integrations" && selectedPracticeId && (
        <IntegrationsTab
          companyId={selectedPracticeId}
          inviteMutation={inviteMutation}
          inviteError={inviteError}
          inviteSnippet={inviteSnippet}
          snippetCopied={snippetCopied}
          snippetCopyDelightId={snippetCopyDelightId}
          setSnippetCopied={setSnippetCopied}
          setSnippetCopyDelightId={setSnippetCopyDelightId}
        />
      )}

      {activeTab === "webhooks" && selectedPracticeId && (
        <WebhooksTab companyId={selectedPracticeId} />
      )}

      {activeTab === "notifications" && selectedPracticeId && (
        <NotificationsTab companyId={selectedPracticeId} />
      )}

      {activeTab === "security" && (
        <SecurityTab companyId={selectedPracticeId} />
      )}
    </div>
  );
}

// ── General Tab ───────────────────────────────────────────────────────

function GeneralTab({
  companyName,
  setCompanyName,
  description,
  setDescription,
  brandColor,
  setBrandColor,
  selectedPractice,
  settingsMutation,
  archiveMutation,
  companies,
  selectedPracticeId,
  setSelectedPracticeId,
  generalMutation,
}: {
  companyName: string;
  setCompanyName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  brandColor: string;
  setBrandColor: (v: string) => void;
  selectedPractice: NonNullable<ReturnType<typeof usePractice>["selectedPractice"]>;
  settingsMutation: { mutate: (v: boolean) => void; isPending: boolean };
  generalMutation: { mutate: (v: { name: string; description: string | null; brandColor: string | null }) => void; isPending: boolean; isSuccess: boolean; isError: boolean; error: Error | null };
  archiveMutation: { mutate: (v: { companyId: string; nextCompanyId: string | null }) => void; isPending: boolean; isError: boolean; error: Error | null };
  companies: ReturnType<typeof usePractice>["companies"];
  selectedPracticeId: string | null;
  setSelectedPracticeId: (id: string) => void;
}) {
  const confirm = useConfirm();
  return (
    <div className="space-y-6">
      {/* Two-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Practice Information */}
        <SectionCard icon={Building2} title="Practice Information" subtitle="Basic information about your practice">
          <div className="space-y-4">
            <Field label="Practice Name" hint="The display name for your practice.">
              <input
                className="w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm outline-none focus:border-primary transition-colors"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </Field>
            <Field label="Description" hint="Optional description shown in the practice profile.">
              <input
                className="w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm outline-none focus:border-primary transition-colors"
                type="text"
                value={description}
                placeholder="Optional practice description"
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
          </div>
        </SectionCard>

        {/* Agent Creation */}
        <SectionCard icon={Bot} title="Agent Configuration" subtitle="How agents are created and managed">
          <div className="space-y-4">
            <ToggleField
              label="Require admin approval for new agents"
              hint="New agents stay pending until approved by an admin."
              checked={!!selectedPractice.requireBoardApprovalForNewAgents}
              onChange={(v) => settingsMutation.mutate(v)}
            />
          </div>
        </SectionCard>

        {/* Monthly Budget */}
        <SectionCard icon={DollarSign} title="Monthly Budget" subtitle="AI agent spend limits for cost management">
          <BudgetSection selectedPractice={selectedPractice} selectedPracticeId={selectedPracticeId} />
        </SectionCard>

        {/* Practice Logo / Appearance */}
        <SectionCard icon={Image} title="Practice Logo" subtitle="Your logo will appear on documents and forms">
          <div className="flex items-center gap-5">
            <img
              src="/brands/vip-medical-group.png"
              alt={selectedPractice.name}
              className="h-16 w-auto object-contain shrink-0"
            />
            <div className="flex-1 space-y-1">
              <p className="text-sm font-medium">{selectedPractice.name}</p>
              <p className="text-xs text-muted-foreground">
                PNG · 895 × 273 px
              </p>
            </div>
          </div>
        </SectionCard>

        {/* Danger Zone */}
        <SectionCard icon={Shield} title="Danger Zone" subtitle="Irreversible actions" variant="destructive">
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Archive this practice to hide it from the sidebar. This persists in
              the database.
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="destructive"
                disabled={
                  archiveMutation.isPending ||
                  selectedPractice.status === "archived"
                }
                onClick={async () => {
                  if (!selectedPracticeId) return;
                  const confirmed = await confirm({
                    title: `Archive practice "${selectedPractice.name}"?`,
                    description: "It will be hidden from the sidebar.",
                    destructive: true,
                  });
                  if (!confirmed) return;
                  const nextCompanyId =
                    companies.find(
                      (company) =>
                        company.id !== selectedPracticeId &&
                        company.status !== "archived",
                    )?.id ?? null;
                  archiveMutation.mutate({
                    companyId: selectedPracticeId,
                    nextCompanyId,
                  });
                }}
              >
                {archiveMutation.isPending
                  ? "Archiving..."
                  : selectedPractice.status === "archived"
                    ? "Already archived"
                    : "Archive practice"}
              </Button>
              {archiveMutation.isError && (
                <span className="text-xs text-destructive">
                  {archiveMutation.error instanceof Error
                    ? archiveMutation.error.message
                    : "Failed to archive practice"}
                </span>
              )}
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Save confirmation */}
      {generalMutation.isSuccess && (
        <div className="flex items-center gap-1.5 text-xs text-emerald-600">
          <Check className="h-3.5 w-3.5" />
          Changes saved
        </div>
      )}
      {generalMutation.isError && (
        <span className="text-xs text-destructive">
          {generalMutation.error instanceof Error
            ? generalMutation.error.message
            : "Failed to save"}
        </span>
      )}
    </div>
  );
}

// ── Integrations Tab ──────────────────────────────────────────────────

function IntegrationRow({
  initials,
  initialsColor,
  name,
  description,
  status,
  statusColor,
  action,
}: {
  initials: string;
  initialsColor: string;
  name: string;
  description: string;
  status: string;
  statusColor: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between px-3 py-3 border-t border-border/50">
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`h-9 w-9 rounded-lg flex items-center justify-center text-white text-xs font-semibold shrink-0 ${initialsColor}`}
        >
          {initials}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{name}</div>
          <div className="text-xs text-muted-foreground truncate">{description}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-xs font-medium ${statusColor}`}>{status}</span>
        {action}
      </div>
    </div>
  );
}

const STATUS_META: Record<
  IntegrationStatus,
  { label: string; color: string }
> = {
  configured: { label: "Configured", color: "text-emerald-600" },
  not_configured: { label: "Not configured", color: "text-muted-foreground" },
  coming_soon: { label: "Coming soon", color: "text-muted-foreground" },
};

const CATEGORY_META: Record<
  IntegrationProvider["category"],
  { title: string; subtitle: string }
> = {
  clearinghouse: {
    title: "Clearinghouse & Payer Connections",
    subtitle: "Real-time API connections for eligibility, claims, and prior authorization",
  },
  ehr: {
    title: "EHR Integrations",
    subtitle: "Electronic Health Record connections per practice",
  },
  other: {
    title: "Other Integrations",
    subtitle: "Additional third-party connections",
  },
};

const PROVIDER_INITIALS: Record<string, { initials: string; color: string }> = {
  availity: { initials: "A", color: "bg-blue-600" },
  unitedhealthcare: { initials: "U", color: "bg-violet-600" },
  waystar: { initials: "W", color: "bg-slate-500" },
  nextgen: { initials: "N", color: "bg-blue-700" },
  epic: { initials: "E", color: "bg-red-600" },
  cerner: { initials: "C", color: "bg-orange-600" },
  athena: { initials: "A", color: "bg-purple-600" },
  amazon_connect: { initials: "AC", color: "bg-orange-500" },
  hubspot: { initials: "HS", color: "bg-orange-600" },
  twilio: { initials: "TW", color: "bg-red-500" },
};

function ProviderConfigForm({
  provider,
  companyId,
  onClose,
}: {
  provider: IntegrationProvider;
  companyId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>(
    () => ({ ...(provider.state.config ?? {}) }),
  );

  const saveMutation = useMutation({
    mutationFn: () =>
      integrationsApi.updateProvider(companyId, provider.id, {
        enabled: true,
        config: values,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations", companyId] });
      onClose();
    },
  });

  return (
    <div className="mt-2 rounded-md border border-border bg-muted/20 p-3 space-y-3">
      {(provider.fields ?? []).map((f) => (
        <Field key={f.key} label={f.label} hint={`Stored per-practice (no secrets logged).`}>
          <input
            className="w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm outline-none focus:border-primary transition-colors"
            type="text"
            value={values[f.key] ?? ""}
            onChange={(e) =>
              setValues((prev) => ({ ...prev, [f.key]: e.target.value }))
            }
          />
        </Field>
      ))}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? "Saving..." : "Save"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        {saveMutation.isError && (
          <span className="text-xs text-destructive">
            {saveMutation.error instanceof Error
              ? saveMutation.error.message
              : "Failed to save"}
          </span>
        )}
      </div>
    </div>
  );
}

function ProviderRow({
  provider,
  companyId,
}: {
  provider: IntegrationProvider;
  companyId: string;
}) {
  const [configuring, setConfiguring] = useState(false);
  const visual = PROVIDER_INITIALS[provider.id] ?? {
    initials: provider.name.slice(0, 1),
    color: "bg-slate-500",
  };
  const meta = STATUS_META[provider.state.status];
  const canConfigure =
    provider.state.status !== "coming_soon" && (provider.fields?.length ?? 0) > 0;

  return (
    <div>
      <IntegrationRow
        initials={visual.initials}
        initialsColor={visual.color}
        name={provider.name}
        description={provider.description}
        status={meta.label}
        statusColor={meta.color}
        action={
          canConfigure ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfiguring((v) => !v)}
            >
              {configuring ? "Close" : "Configure"}
            </Button>
          ) : undefined
        }
      />
      {configuring && canConfigure && (
        <ProviderConfigForm
          provider={provider}
          companyId={companyId}
          onClose={() => setConfiguring(false)}
        />
      )}
    </div>
  );
}

function IntegrationsTab({
  companyId,
  inviteMutation,
  inviteError,
  inviteSnippet,
  snippetCopied,
  snippetCopyDelightId,
  setSnippetCopied,
  setSnippetCopyDelightId,
}: {
  companyId: string;
  inviteMutation: { mutate: () => void; isPending: boolean };
  inviteError: string | null;
  inviteSnippet: string | null;
  snippetCopied: boolean;
  snippetCopyDelightId: number;
  setSnippetCopied: (v: boolean) => void;
  setSnippetCopyDelightId: (fn: (prev: number) => number) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["integrations", companyId],
    queryFn: () => integrationsApi.get(companyId),
  });

  const providers = data?.providers ?? [];
  const categories: Array<IntegrationProvider["category"]> = [
    "clearinghouse",
    "ehr",
    "other",
  ];

  return (
    <div className="space-y-8">
      {categories.map((category) => {
        const rows = providers.filter((p) => p.category === category);
        if (rows.length === 0 && !isLoading) return null;
        const meta = CATEGORY_META[category];
        return (
          <SectionCard key={category} icon={Link2} title={meta.title} subtitle={meta.subtitle}>
            <div className="space-y-0">
              {isLoading ? (
                <div className="text-sm text-muted-foreground py-4">Loading…</div>
              ) : (
                rows.map((provider) => (
                  <ProviderRow
                    key={provider.id}
                    provider={provider}
                    companyId={companyId}
                  />
                ))
              )}
            </div>
          </SectionCard>
        );
      })}

      {/* Analytics & Data */}
      <LookerIntegrationSection />

      {/* AI Adapter Compliance */}
      <AdapterComplianceSection companyId={companyId} />

      {/* Gateway Invites */}
      <SectionCard icon={Ticket} title="Gateway Agent Invites" subtitle="Generate invite snippets for gateway agents">
        <div className="space-y-3">
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-muted-foreground">
              Generate a Gateway agent invite snippet.
            </span>
            <HintIcon text="Creates a short-lived Gateway agent invite and renders a copy-ready prompt." />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={() => inviteMutation.mutate()}
              disabled={inviteMutation.isPending}
            >
              {inviteMutation.isPending
                ? "Generating..."
                : "Generate Gateway Invite Prompt"}
            </Button>
          </div>
          {inviteError && (
            <p className="text-sm text-destructive">{inviteError}</p>
          )}
          {inviteSnippet && (
            <div className="rounded-md border border-border bg-muted/30 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-medium text-muted-foreground">
                  Gateway Invite Prompt
                </div>
                {snippetCopied && (
                  <span
                    key={snippetCopyDelightId}
                    className="flex items-center gap-1 text-xs text-green-600 animate-pulse"
                  >
                    <Check className="h-3 w-3" />
                    Copied
                  </span>
                )}
              </div>
              <div className="mt-2 space-y-2">
                <textarea
                  className="h-[28rem] w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs outline-none"
                  value={inviteSnippet}
                  readOnly
                />
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(inviteSnippet);
                        setSnippetCopied(true);
                        setSnippetCopyDelightId((prev) => prev + 1);
                        setTimeout(() => setSnippetCopied(false), 2000);
                      } catch {
                        /* clipboard may not be available */
                      }
                    }}
                  >
                    {snippetCopied ? "Copied snippet" : "Copy snippet"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}

// ── Notifications Tab ─────────────────────────────────────────────────

type NotificationCategoryKey = keyof NotificationPrefs;

function NotificationsTab({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["notification-prefs", companyId],
    queryFn: () => notificationPrefsApi.get(companyId),
  });

  const updateMutation = useMutation({
    mutationFn: (prefs: Partial<NotificationPrefs>) =>
      notificationPrefsApi.update(companyId, prefs),
    onSuccess: (res) => {
      queryClient.setQueryData(["notification-prefs", companyId], res);
    },
  });

  const prefs = data?.prefs;

  const categories: Array<{
    key: NotificationCategoryKey;
    label: string;
    description: string;
  }> = [
    { key: "agentErrors", label: "Agent Errors", description: "Circuit breaker trips, dead letter events, run failures" },
    { key: "approvals", label: "Approvals", description: "New approval requests and status changes" },
    { key: "issueUpdates", label: "Issue Updates", description: "Assigned issues, status changes, comments" },
    { key: "systemAlerts", label: "System Alerts", description: "Health checks, deployment status, security events" },
  ];

  const toggleEmail = (key: NotificationCategoryKey) => {
    if (!prefs) return;
    updateMutation.mutate({ [key]: { email: !prefs[key].email } } as Partial<NotificationPrefs>);
  };

  return (
    <div className="space-y-8">
      <SectionCard
        icon={Bell}
        title="Notification Channels"
        subtitle="Choose how you want to receive notifications. Email is delivered via SES; there is no push backend yet."
      >
        {isLoading || !prefs ? (
          <div className="text-sm text-muted-foreground py-4">Loading…</div>
        ) : (
          <div className="space-y-1">
            {/* Header */}
            <div className="flex items-center justify-end gap-6 pb-2 pr-1 text-xs text-muted-foreground">
              <span className="w-14 text-center">Email</span>
              <span className="w-14 text-center opacity-50">Push</span>
            </div>
            {/* Rows */}
            {categories.map(({ key, label, description }) => (
              <div key={key} className="flex items-center justify-between py-2.5 border-t border-border/50">
                <div>
                  <div className="text-sm font-medium">{label}</div>
                  <div className="text-xs text-muted-foreground">{description}</div>
                </div>
                <div className="flex items-center gap-6 pr-1">
                  <div className="w-14 flex justify-center">
                    <ToggleSwitch
                      checked={prefs[key].email}
                      onChange={() => toggleEmail(key)}
                    />
                  </div>
                  <div className="w-14 flex flex-col items-center justify-center">
                    <ToggleSwitch checked={false} disabled onChange={() => {}} />
                  </div>
                </div>
              </div>
            ))}
            <p className="pt-2 text-[11px] text-muted-foreground">
              Push notifications are not yet available — no push delivery backend is configured.
            </p>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ── Security Tab ──────────────────────────────────────────────────────

function formatUserAgent(ua: string | null | undefined): string {
  if (!ua) return "Unknown device";
  const browser = /Edg/.test(ua)
    ? "Edge"
    : /Chrome/.test(ua)
      ? "Chrome"
      : /Firefox/.test(ua)
        ? "Firefox"
        : /Safari/.test(ua)
          ? "Safari"
          : "Browser";
  const os = /Mac OS X|Macintosh/.test(ua)
    ? "macOS"
    : /Windows/.test(ua)
      ? "Windows"
      : /Linux/.test(ua)
        ? "Linux"
        : /iPhone|iPad|iOS/.test(ua)
          ? "iOS"
          : /Android/.test(ua)
            ? "Android"
            : "Unknown OS";
  return `${browser} on ${os}`;
}

function ChangePasswordForm() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [revokeOthers, setRevokeOthers] = useState(true);

  const mutation = useMutation({
    mutationFn: () =>
      authApi.changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions: revokeOthers,
      }),
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
    },
  });

  return (
    <div className="mt-3 rounded-md border border-border bg-muted/20 p-3 space-y-3">
      <Field label="Current Password" hint="Your existing password.">
        <input
          className="w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm outline-none focus:border-primary transition-colors"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
      </Field>
      <Field label="New Password" hint="At least 8 characters.">
        <input
          className="w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm outline-none focus:border-primary transition-colors"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
      </Field>
      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={revokeOthers}
          onChange={(e) => setRevokeOthers(e.target.checked)}
        />
        Sign out of all other devices
      </label>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => mutation.mutate()}
          disabled={
            mutation.isPending ||
            !currentPassword ||
            newPassword.length < 8
          }
        >
          {mutation.isPending ? "Updating…" : "Update Password"}
        </Button>
        {mutation.isSuccess && (
          <span className="flex items-center gap-1.5 text-xs text-emerald-600">
            <Check className="h-3.5 w-3.5" />
            Password updated
          </span>
        )}
        {mutation.isError && (
          <span className="text-xs text-destructive">
            {mutation.error instanceof Error
              ? mutation.error.message
              : "Failed to update password"}
          </span>
        )}
      </div>
    </div>
  );
}

function SecurityTab({ companyId }: { companyId: string | null }) {
  const queryClient = useQueryClient();
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const { data: session } = useQuery({
    queryKey: ["auth-session"],
    queryFn: () => authApi.getSession(),
  });

  const { data: sessions = [] } = useQuery({
    queryKey: ["auth-sessions"],
    queryFn: () => authApi.listSessions(),
  });

  const { data: members = [] } = useQuery({
    queryKey: ["company-members", companyId],
    queryFn: () => companiesApi.members(companyId!),
    enabled: !!companyId,
  });

  const revokeMutation = useMutation({
    mutationFn: () => authApi.revokeOtherSessions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth-sessions"] });
    },
  });

  const user = session?.user;
  const currentSessionId = session?.session.id;
  const myRole =
    user?.email != null
      ? members.find((m) => m.email === user.email)?.role ?? null
      : null;

  return (
    <div className="space-y-8">
      <SectionCard icon={Lock} title="Account Information" subtitle="Your login and identity details">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-muted-foreground">Name</div>
            <div className="text-sm font-medium mt-0.5">{user?.name ?? "—"}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Email</div>
            <div className="text-sm font-medium mt-0.5">{user?.email ?? "—"}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Role</div>
            <div className="text-sm font-medium mt-0.5 capitalize">
              {myRole ?? "—"}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Account Status</div>
            <div className="mt-0.5">
              <span className="text-xs bg-emerald-500/10 text-emerald-600 px-2 py-0.5 rounded-full font-medium">
                Active
              </span>
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={Lock} title="Password" subtitle="Manage your password">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm">Change your password</div>
            <div className="text-xs text-muted-foreground">
              Update your password and optionally sign out other devices.
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowPasswordForm((v) => !v)}
          >
            {showPasswordForm ? "Cancel" : "Reset Password"}
          </Button>
        </div>
        {showPasswordForm && <ChangePasswordForm />}
      </SectionCard>

      <SectionCard icon={Monitor} title="Active Sessions" subtitle="Devices currently logged into your account">
        <div className="space-y-3">
          {sessions.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No active sessions found.
            </div>
          ) : (
            sessions.map((s) => {
              const isCurrent = s.id === currentSessionId;
              return (
                <div
                  key={s.id}
                  className={`flex items-center gap-3 rounded-md px-3 py-2.5 ${
                    isCurrent
                      ? "bg-emerald-500/5 border border-emerald-500/20"
                      : "border border-border/50"
                  }`}
                >
                  <Monitor
                    className={`h-4 w-4 shrink-0 ${
                      isCurrent ? "text-emerald-600" : "text-muted-foreground"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {formatUserAgent(s.userAgent)}
                      </span>
                      {isCurrent && (
                        <span className="text-[10px] bg-emerald-500/10 text-emerald-600 px-1.5 py-0.5 rounded font-medium">
                          Current Session
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {s.ipAddress ? `${s.ipAddress} · ` : ""}
                      {s.createdAt
                        ? `signed in ${new Date(s.createdAt).toLocaleString()}`
                        : ""}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div className="flex items-center justify-between pt-2 border-t border-border/50">
            <div>
              <div className="text-sm">Sign out of all other devices</div>
              <div className="text-xs text-muted-foreground">
                Revoke all sessions except the current one
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => revokeMutation.mutate()}
              disabled={revokeMutation.isPending || sessions.length <= 1}
            >
              {revokeMutation.isPending ? "Revoking…" : "Revoke Others"}
            </Button>
          </div>
          {revokeMutation.isError && (
            <span className="text-xs text-destructive">
              {revokeMutation.error instanceof Error
                ? revokeMutation.error.message
                : "Failed to revoke sessions"}
            </span>
          )}
        </div>
      </SectionCard>
    </div>
  );
}

// ── Shared Components ─────────────────────────────────────────────────

// ── Webhooks Tab ─────────────────────────────────────────────────────

function WebhooksTab({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [showCreate, setShowCreate] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newEventType, setNewEventType] = useState("ehr_update");
  const [newLabel, setNewLabel] = useState("");
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);

  const { data: webhooks = [], isLoading } = useQuery({
    queryKey: ["webhooks", companyId],
    queryFn: () => webhooksApi.list(companyId),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      webhooksApi.create(companyId, {
        eventType: newEventType,
        url: newUrl.trim(),
        label: newLabel.trim() || undefined,
      }),
    onSuccess: (result: WebhookRegistrationWithSecret) => {
      setCreatedSecret(result.secret);
      setNewUrl("");
      setNewLabel("");
      setShowCreate(false);
      queryClient.invalidateQueries({ queryKey: ["webhooks", companyId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (webhookId: string) => webhooksApi.remove(webhookId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["webhooks", companyId] });
    },
  });

  const EVENT_TYPES = [
    "ehr_update", "claim_status_change", "prior_auth_decision",
    "lab_result", "eligibility_response", "appointment_reminder",
    "patient_message", "referral_update",
  ];

  return (
    <div className="space-y-6">
      {/* Secret display (shown once after creation) */}
      {createdSecret && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-700">Webhook Secret — Save Now</span>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            This HMAC secret will only be shown once. Store it securely.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-muted/50 rounded px-3 py-2 font-mono break-all">
              {createdSecret}
            </code>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(createdSecret);
                setSecretCopied(true);
                setTimeout(() => setSecretCopied(false), 2000);
              }}
            >
              {secretCopied ? "Copied!" : "Copy"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCreatedSecret(null)}>
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* Header + Create button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold">Webhook Registrations</h2>
          <p className="text-xs text-muted-foreground">
            External systems send events here to trigger agent execution
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? "Cancel" : "+ Register Webhook"}
        </Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="rounded-lg border border-border p-4 space-y-3">
          <Field label="Endpoint URL" hint="The URL that will receive webhook events">
            <input
              className="w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm outline-none focus:border-primary transition-colors"
              type="url"
              placeholder="https://your-system.example.com/webhooks"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Event Type" hint="Which event triggers this webhook">
              <select
                className="w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm outline-none focus:border-primary transition-colors"
                value={newEventType}
                onChange={(e) => setNewEventType(e.target.value)}
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Label" hint="Optional description">
              <input
                className="w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm outline-none focus:border-primary transition-colors"
                type="text"
                placeholder="e.g. NextGen EHR Updates"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
              />
            </Field>
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending || !newUrl.trim()}
            >
              {createMutation.isPending ? "Creating..." : "Create Webhook"}
            </Button>
          </div>
        </div>
      )}

      {/* Webhook list */}
      {isLoading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading webhooks...</div>
      ) : webhooks.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <Webhook className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">No webhooks registered yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Register a webhook to receive events from external systems
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-border divide-y divide-border">
          {webhooks.map((wh) => (
            <div key={wh.id} className="flex items-center justify-between px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">
                    {wh.label || wh.url}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                    wh.active
                      ? "bg-emerald-500/10 text-emerald-600"
                      : "bg-muted text-muted-foreground"
                  }`}>
                    {wh.active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                  <span>{wh.eventType.replace(/_/g, " ")}</span>
                  <span className="truncate max-w-[300px]">{wh.url}</span>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive shrink-0"
                onClick={async () => {
                  if (
                    await confirm({
                      title: "Deactivate this webhook?",
                      destructive: true,
                    })
                  ) {
                    deleteMutation.mutate(wh.id);
                  }
                }}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Budget Section ───────────────────────────────────────────────────

function BudgetSection({
  selectedPractice,
  selectedPracticeId,
}: {
  selectedPractice: NonNullable<ReturnType<typeof usePractice>["selectedPractice"]>;
  selectedPracticeId: string | null;
}) {
  const queryClient = useQueryClient();
  const currentBudget = (selectedPractice as { budgetMonthlyCents?: number }).budgetMonthlyCents ?? 0;
  const currentSpend = (selectedPractice as { spentMonthlyCents?: number }).spentMonthlyCents ?? 0;
  const [budgetDollars, setBudgetDollars] = useState(String(currentBudget / 100));
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setBudgetDollars(String(currentBudget / 100));
  }, [currentBudget]);

  const budgetMutation = useMutation({
    mutationFn: (cents: number) =>
      companiesApi.update(selectedPracticeId!, { budgetMonthlyCents: cents }),
    onSuccess: () => {
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
    },
  });

  const budgetCents = Math.round(parseFloat(budgetDollars || "0") * 100);
  const utilization = budgetCents > 0 ? Math.min(100, Math.round((currentSpend / budgetCents) * 100)) : 0;
  const utilizationColor =
    utilization >= 90 ? "bg-red-500" : utilization >= 70 ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-2xl font-semibold tabular-nums">
            ${(currentSpend / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            <span className="text-sm font-normal text-muted-foreground ml-1">
              / ${(budgetCents / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })} month
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">{utilization}% utilized</div>
        </div>
      </div>

      {/* Utilization bar */}
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${utilizationColor}`}
          style={{ width: `${utilization}%` }}
        />
      </div>

      {/* Edit budget */}
      {editing ? (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">$</span>
          <input
            className="w-32 rounded-md border border-border bg-transparent px-3 py-1.5 text-sm outline-none focus:border-primary transition-colors tabular-nums"
            type="number"
            min="0"
            step="100"
            value={budgetDollars}
            onChange={(e) => setBudgetDollars(e.target.value)}
            autoFocus
          />
          <span className="text-sm text-muted-foreground">/ month</span>
          <Button
            size="sm"
            onClick={() => budgetMutation.mutate(budgetCents)}
            disabled={budgetMutation.isPending || budgetCents === currentBudget}
          >
            {budgetMutation.isPending ? "Saving..." : "Save"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setBudgetDollars(String(currentBudget / 100)); }}>
            Cancel
          </Button>
        </div>
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="text-xs text-primary hover:underline"
        >
          Edit monthly budget
        </button>
      )}
    </div>
  );
}

function LookerIntegrationSection() {
  const { selectedPracticeId } = usePractice();
  const [baseUrl, setBaseUrl] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [testStatus, setTestStatus] = useState<"idle" | "testing" | "success" | "error">("idle");
  const [testMessage, setTestMessage] = useState("");
  const [saved, setSaved] = useState(false);

  // Load existing Looker config from the dedicated looker-config endpoint
  // (the company GET never returns lookerConfig — that was the prefill bug).
  const { data: lookerConfig } = useQuery({
    queryKey: ["looker-config", selectedPracticeId],
    queryFn: async () => {
      const res = await fetch(
        `/api/companies/${selectedPracticeId}/looker-config`,
        { credentials: "include", headers: { Accept: "application/json" } },
      );
      if (!res.ok) return { baseUrl: "", clientId: "", configured: false };
      return res.json() as Promise<{
        baseUrl: string;
        clientId: string;
        configured: boolean;
      }>;
    },
    enabled: !!selectedPracticeId,
  });

  useEffect(() => {
    if (lookerConfig) {
      setBaseUrl(lookerConfig.baseUrl ?? "");
      setClientId(lookerConfig.clientId ?? "");
      // Secret is masked server-side; only show placeholder if configured
      if (lookerConfig.configured) {
        setClientSecret("••••••••••••••••");
      }
    }
  }, [lookerConfig]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, string> = { baseUrl, clientId };
      // Only send secret if it was changed (not the masked placeholder)
      if (clientSecret && !clientSecret.startsWith("••")) {
        payload.clientSecret = clientSecret;
      }
      const res = await fetch(`/api/companies/${selectedPracticeId}/looker-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to save");
      return res.json();
    },
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const testConnection = async () => {
    setTestStatus("testing");
    setTestMessage("");
    try {
      const res = await fetch(`/api/companies/${selectedPracticeId}/looker-config/test`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setTestStatus("success");
        setTestMessage(data.message ?? "Connected successfully");
      } else {
        setTestStatus("error");
        setTestMessage(data.error ?? "Connection failed");
      }
    } catch {
      setTestStatus("error");
      setTestMessage("Network error — check your Looker URL");
    }
  };

  return (
    <SectionCard icon={BarChart3} title="Analytics & Data" subtitle="Business intelligence and reporting connections">
      <div className="space-y-0">
        <IntegrationRow
          initials="L"
          initialsColor="bg-indigo-600"
          name="Looker"
          description="Google Looker — dashboards, explores, and LookML metrics"
          status={baseUrl ? "Configured" : "Not Configured"}
          statusColor={baseUrl ? "text-emerald-600" : "text-muted-foreground"}
        />
      </div>

      <div className="mt-4 border-t border-border pt-4 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-foreground block mb-1.5">
              Looker Base URL
            </label>
            <input
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground/60"
              placeholder="https://your-instance.cloud.looker.com"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground block mb-1.5">
              Client ID
            </label>
            <input
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground/60"
              placeholder="API3 Client ID"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground block mb-1.5">
              Client Secret
            </label>
            <div className="relative">
              <input
                className="w-full rounded-md border border-border bg-background px-3 py-2 pr-10 text-sm font-mono outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground/60"
                type={showSecret ? "text" : "password"}
                placeholder="API3 Client Secret"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowSecret(!showSecret)}
              >
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={!baseUrl || !clientId || saveMutation.isPending}
          >
            {saveMutation.isPending ? "Saving..." : saved ? "Saved ✓" : "Save Credentials"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={testConnection}
            disabled={!baseUrl || !clientId || testStatus === "testing"}
          >
            {testStatus === "testing" ? "Testing..." : "Test Connection"}
          </Button>
          {testStatus === "success" && (
            <span className="flex items-center gap-1.5 text-sm text-emerald-600">
              <ShieldCheck className="h-4 w-4" />
              {testMessage}
            </span>
          )}
          {testStatus === "error" && (
            <span className="text-sm text-destructive">{testMessage}</span>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

function SectionCard({
  icon: Icon,
  title,
  subtitle,
  variant,
  children,
}: {
  icon: typeof Settings;
  title: string;
  subtitle: string;
  variant?: "destructive";
  children: React.ReactNode;
}) {
  const borderClass =
    variant === "destructive"
      ? "border-destructive/40 bg-destructive/5"
      : "border-border";

  return (
    <div className={`rounded-lg border ${borderClass} p-5`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon
          className={`h-4.5 w-4.5 ${
            variant === "destructive"
              ? "text-destructive"
              : "text-primary"
          }`}
        />
        <div>
          <h3 className="text-sm font-semibold leading-tight">{title}</h3>
          <p className="text-xs text-muted-foreground leading-tight">
            {subtitle}
          </p>
        </div>
      </div>
      {children}
    </div>
  );
}

function ToggleSwitch({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"
      } ${
        checked ? "bg-primary" : "bg-muted-foreground/30"
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
          checked ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  );
}

// ── Adapter Compliance ────────────────────────────────────────────────

const DEFAULT_ENABLED_ADAPTERS = ["bedrock"];

/**
 * The agent builder (AgentConfigForm) gates the adapter dropdown off a localStorage
 * mirror of this set, keyed `ligament.enabledAdapters`, and listens for the storage
 * event. The source of truth now lives per-practice in settings.integrations.adapters;
 * we mirror the backend value into localStorage so that consumer keeps working without
 * its own network call.
 */
const ADAPTER_ENABLED_MIRROR_KEY = "ligament.enabledAdapters";

function mirrorAdaptersToLocalStorage(enabled: string[]) {
  try {
    localStorage.setItem(ADAPTER_ENABLED_MIRROR_KEY, JSON.stringify(enabled));
    window.dispatchEvent(
      new StorageEvent("storage", { key: ADAPTER_ENABLED_MIRROR_KEY }),
    );
  } catch {
    /* ignore */
  }
}

const HIPAA_SAFE_ADAPTERS = new Set(["bedrock"]);

const COMPLIANCE_ADAPTERS = AGENT_ADAPTER_TYPES.filter(
  (t) => !["process", "http"].includes(t),
);

function AdapterComplianceSection({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["integrations", companyId],
    queryFn: () => integrationsApi.get(companyId),
  });

  // null = practice hasn't saved a set yet → fall back to the default.
  const enabledList = data?.adapters.enabled ?? DEFAULT_ENABLED_ADAPTERS;
  const enabled = new Set(enabledList);

  // Keep the localStorage mirror (consumed by AgentConfigForm) in sync.
  useEffect(() => {
    mirrorAdaptersToLocalStorage(enabledList);
  }, [enabledList.join(",")]);

  const updateMutation = useMutation({
    mutationFn: (next: string[]) => integrationsApi.updateAdapters(companyId, next),
    onSuccess: (res) => {
      queryClient.setQueryData(["integrations", companyId], res);
      mirrorAdaptersToLocalStorage(res.adapters.enabled ?? DEFAULT_ENABLED_ADAPTERS);
    },
  });

  const toggle = (type: string) => {
    const next = new Set(enabled);
    if (next.has(type)) {
      if (next.size <= 1) return;
      next.delete(type);
    } else {
      next.add(type);
    }
    updateMutation.mutate([...next]);
  };

  const activeCount = enabled.size;
  const totalCount = COMPLIANCE_ADAPTERS.length;

  return (
    <div className="space-y-4">
      <SectionCard
        icon={Shield}
        title="Adapter Compliance (BAA Status)"
        subtitle="Enable adapters only after a Business Associate Agreement (BAA) is signed"
      >
        <div className="space-y-3">
          {/* Status bar */}
          <div className="flex items-center gap-2 rounded-md bg-emerald-500/5 border border-emerald-500/20 px-3 py-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            <span className="text-sm text-emerald-700">
              {activeCount}/{totalCount} adapters enabled
            </span>
          </div>

          {/* Adapter list */}
          <div className="space-y-0.5">
            {COMPLIANCE_ADAPTERS.map((type) => {
              const isEnabled = enabled.has(type);
              const isSafe = HIPAA_SAFE_ADAPTERS.has(type);
              return (
                <button
                  key={type}
                  onClick={() => toggle(type)}
                  className="flex items-center justify-between w-full px-3 py-2.5 text-sm rounded-md hover:bg-accent/50 transition-colors"
                >
                  <span className="flex items-center gap-2.5">
                    {isEnabled ? (
                      <ShieldCheck className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <Shield className="h-4 w-4 text-muted-foreground/40" />
                    )}
                    <span
                      className={
                        isEnabled ? "text-foreground" : "text-muted-foreground"
                      }
                    >
                      {adapterLabels[type] ?? type}
                    </span>
                    {isSafe && (
                      <span className="text-[10px] bg-emerald-500/10 text-emerald-600 px-1.5 py-0.5 rounded-full">
                        No BAA needed
                      </span>
                    )}
                  </span>
                  <span
                    className={`text-xs ${isEnabled ? "text-emerald-600" : "text-muted-foreground/50"}`}
                  >
                    {isEnabled ? "Enabled" : "Requires BAA"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

// ── Snippet helpers ───────────────────────────────────────────────────

function buildAgentSnippet(input: AgentSnippetInput) {
  const candidateUrls = buildCandidateOnboardingUrls(input);
  const resolutionTestUrl = buildResolutionTestUrl(input);

  const candidateList =
    candidateUrls.length > 0
      ? candidateUrls.map((u) => `- ${u}`).join("\n")
      : "- (No candidate URLs available yet.)";

  const connectivityBlock =
    candidateUrls.length === 0
      ? `No candidate URLs are available. Ask your user to configure a reachable hostname in Ligament, then retry.
Suggested steps:
- choose a hostname that resolves to the Ligament host from your runtime
- run: pnpm ligament allowed-hostname <host>
- restart Ligament
- verify with: curl -fsS http://<host>:3100/api/health
- regenerate this invite snippet`
      : `If none are reachable, ask your user to add a reachable hostname in Ligament, restart, and retry.
Suggested command:
- pnpm ligament allowed-hostname <host>
Then verify with: curl -fsS <base-url>/api/health`;

  const resolutionLine = resolutionTestUrl
    ? `\nYou MUST test Ligament-to-gateway reachability, call: ${resolutionTestUrl}?url=<urlencoded-gateway-url> (using the hostname that worked above). Do not assume your 172.x is necessarily reachable from Ligament. Test it. `
    : "";

  return `You're invited to join a Ligament organization.

The URLs you should try are:
${candidateList}

Connectivity guidance:
If you are running on a different machine than Ligament, Ligament must be reachable at one of the hostnames used above.
Verify the hostname works from your runtime with: GET <base-url>/api/health

${connectivityBlock}

For your "agentName", just use your own agent name (Gateway or whatever your name is) and send it directly.
You also need to send a gateway URL that Ligament can access. Check your config and include that URL.${resolutionLine}

---

This onboarding flow is for Gateway Gateway.
When you submit the join request, use:
- \`adapterType: "gateway"\`
- \`agentDefaultsPayload.url\` as your \`ws://\` or \`wss://\` gateway URL
- \`agentDefaultsPayload.headers["x-gateway-token"]\` with your gateway token
- (legacy accepted) \`agentDefaultsPayload.headers["x-gateway-auth"]\`
- Keep device auth enabled (recommended). If \`devicePrivateKeyPem\` is omitted, Ligament will generate and persist one during join so pairing approvals remain stable.
- Only use \`disableDeviceAuth=true\` for special environments where pairing cannot be completed.

Do NOT use \`/v1/responses\` or \`/hooks/*\` in this join flow.

Before you do anything, please respond to your user that you understand the instructions and you're going to work on them. Then do the step above in another session called "ligament-onboarding" and then tell your user when you're done. Update your user in intermediate steps along the way so they know what's going on.

Then after you've connected to Ligament (exchanged keys etc.) you MUST review and follow the onboarding instructions in onboarding.txt they give you.

`;
}

function buildCandidateOnboardingUrls(input: AgentSnippetInput): string[] {
  const candidates = (input.connectionCandidates ?? [])
    .map((candidate) => candidate.trim())
    .filter(Boolean);
  const urls = new Set<string>();
  let onboardingUrl: URL | null = null;

  try {
    onboardingUrl = new URL(input.onboardingTextUrl);
    urls.add(onboardingUrl.toString());
  } catch {
    const trimmed = input.onboardingTextUrl.trim();
    if (trimmed) {
      urls.add(trimmed);
    }
  }

  if (!onboardingUrl) {
    for (const candidate of candidates) {
      urls.add(candidate);
    }
    return Array.from(urls);
  }

  const onboardingPath = `${onboardingUrl.pathname}${onboardingUrl.search}`;
  for (const candidate of candidates) {
    try {
      const base = new URL(candidate);
      urls.add(`${base.origin}${onboardingPath}`);
    } catch {
      urls.add(candidate);
    }
  }

  return Array.from(urls);
}

function buildResolutionTestUrl(input: AgentSnippetInput): string | null {
  const explicit = input.testResolutionUrl?.trim();
  if (explicit) return explicit;

  try {
    const onboardingUrl = new URL(input.onboardingTextUrl);
    const testPath = onboardingUrl.pathname.replace(
      /\/onboarding\.txt$/,
      "/test-resolution",
    );
    return `${onboardingUrl.origin}${testPath}`;
  } catch {
    return null;
  }
}
